<template>
  <div class="location-page">
    <div class="hero-section">
      <div class="container">
        <h1>Tandarts Den Bosch omgeving | Tandzorg Gestel Sint-Michielsgestel</h1>
        <p class="lead">Zoekt u een betrouwbare tandarts in de omgeving van Den Bosch? Tandzorg Gestel in nabijgelegen Sint-Michielsgestel biedt uitstekende tandheelkundige zorg voor patiënten uit 's-Hertogenbosch en omstreken.</p>
      </div>
    </div>

    <div class="content-section">
      <div class="container">
        <div class="row">
          <div class="col-md-8">
            <h2>Waarom kiezen patiënten uit Den Bosch voor Tandzorg Gestel?</h2>
            <p>Veel inwoners van 's-Hertogenbosch (Den Bosch) kiezen bewust voor Tandzorg Gestel in Sint-Michielsgestel. Onze praktijk ligt slechts 10 minuten rijden van het centrum van Den Bosch en biedt voordelen die u in de drukte van de stad niet altijd vindt.</p>

            <h3>Voordelen voor patiënten uit Den Bosch</h3>
            <ul>
              <li><strong>Persoonlijke aandacht</strong> - Geen haast, alle tijd voor u</li>
              <li><strong>Makkelijk parkeren</strong> - Gratis parkeerplaatsen voor de deur</li>
              <li><strong>Korte reisafstand</strong> - Slechts 10 minuten vanaf Den Bosch centrum</li>
              <li><strong>Flexibele afspraken</strong> - Ook buiten de reguliere tijden mogelijk</li>
              <li><strong>Moderne praktijk</strong> - Nieuwste apparatuur en technieken</li>
              <li><strong>Ervaren team</strong> - Jarenlange ervaring in tandheelkunde</li>
            </ul>

            <h2>Bereikbaarheid vanuit Den Bosch</h2>
            <p>Tandzorg Gestel is uitstekend bereikbaar vanuit alle wijken van 's-Hertogenbosch:</p>

            <h3>Met de auto vanuit Den Bosch</h3>
            <ul>
              <li><strong>Vanuit centrum Den Bosch:</strong> Via Vughterstraat richting Vught, dan A2 afslag Sint-Michielsgestel (10 min)</li>
              <li><strong>Vanuit Den Bosch Noord:</strong> Via Ring Noord, A2 afslag Sint-Michielsgestel (12 min)</li>
              <li><strong>Vanuit Den Bosch Oost:</strong> Via Deutersestraat richting A2 afslag Sint-Michielsgestel (8 min)</li>
              <li><strong>Vanuit Rosmalen:</strong> Via A2 zuidwaarts, afslag Sint-Michielsgestel (15 min)</li>
            </ul>

            <h3>Openbaar vervoer vanuit Den Bosch</h3>
            <p>Ook met het openbaar vervoer bent u snel bij ons:</p>
            <ul>
              <li><strong>Bus lijn 20:</strong> Van Den Bosch CS naar Sint-Michielsgestel centrum (25 min)</li>
              <li><strong>Trein + bus:</strong> Trein naar station Vught, dan bus naar Sint-Michielsgestel (20 min)</li>
            </ul>

            <h2>Onze diensten voor patiënten uit Den Bosch</h2>
            <p>We bieden een volledig scala aan tandheelkundige diensten voor onze patiënten uit 's-Hertogenbosch:</p>

            <div class="services-grid">
              <div class="service-item">
                <h4>Preventieve Zorg</h4>
                <p>Regelmatige controles en professionele tandreinigingen om problemen te voorkomen.</p>
                <router-link to="/mondhygiene" class="service-link">Meer over mondhygiëne</router-link>
              </div>
              
              <div class="service-item">
                <h4>Cosmetische Behandelingen</h4>
                <p>Tandenbleken, veneers en andere behandelingen voor een stralende glimlach.</p>
                <router-link to="/cosmetische-tandheelkunde" class="service-link">Meer over cosmetische tandheelkunde</router-link>
              </div>
              
              <div class="service-item">
                <h4>Wortelkanaalbehandelingen</h4>
                <p>Moderne, pijnloze behandelingen om uw eigen tanden te behouden.</p>
                <router-link to="/wortelkanaalbehandeling" class="service-link">Meer over wortelkanaalbehandeling</router-link>
              </div>
            </div>

            <h2>Wat zeggen patiënten uit Den Bosch?</h2>
            <div class="testimonials">
              <blockquote>
                <p>"Na jarenlang naar verschillende tandartsen in Den Bosch te zijn geweest, ben ik zo blij dat ik Tandzorg Gestel heb gevonden. De persoonlijke aandacht en kwaliteit van zorg is uitstekend. De korte rit vanuit Den Bosch is het meer dan waard!"</p>
                <cite>- Sandra K., Den Bosch</cite>
              </blockquote>
              
              <blockquote>
                <p>"Parkeren in Den Bosch is altijd een uitdaging. Bij Tandzorg Gestel parkeer je gratis voor de deur. Plus de behandeling is van topkwaliteit. Echt een aanrader voor mensen uit Den Bosch!"</p>
                <cite>- Mark V., Den Bosch Noord</cite>
              </blockquote>
            </div>

            <h2>Specialisatie in angstpatiënten</h2>
            <p>Veel patiënten uit Den Bosch komen naar ons toe omdat ze elders slechte ervaringen hebben gehad. Wij zijn gespecialiseerd in het behandelen van angstpatiënten en nemen alle tijd om u op uw gemak te stellen.</p>

            <h2>Verzekeringen en kosten</h2>
            <p>Tandzorg Gestel werkt samen met alle Nederlandse zorgverzekeraars. Voor patiënten uit Den Bosch geldt hetzelfde vergoedingensysteem als voor alle Nederlandse patiënten. We helpen graag bij het afhandelen van uw verzekeringsclaims.</p>

            <h2>Spoedhulp voor patiënten uit Den Bosch</h2>
            <p>Heeft u buiten kantooruren spoedeisende tandheelkundige hulp nodig? Voor inwoners van Den Bosch en omgeving verwijzen wij naar:</p>
            <ul>
              <li><strong>Mondzorg Poli 's-Hertogenbosch</strong></li>
              <li>📞 <a href="tel:0900-1515" class="contact-link">0900-1515</a> (24/7 bereikbaar)</li>
              <li>📍 Eekbrouwersweg 4, 's-Hertogenbosch</li>
            </ul>
          </div>

          <div class="col-md-4">
            <div class="appointment-card">
              <h3>Plan uw bezoek vanuit Den Bosch</h3>
              <p>Ontdek waarom steeds meer inwoners van Den Bosch kiezen voor de persoonlijke zorg van Tandzorg Gestel.</p>
              <router-link to="/contact" class="link-btn">Contact opnemen</router-link>
              <router-link to="/afspraak-maken" class="link-btn">Afspraak maken</router-link>
              
              <div class="contact-info">
                <h4>Contact</h4>
                <p>📞 <a href="tel:+31735515644" class="contact-link">073-5515644</a></p>
                <p>✉️ <a href="mailto:info@tandzorggestel.nl" class="contact-link">info@tandzorggestel.nl</a></p>
                <p>📍 Schijndelseweg 13A<br>5271 BK Sint-Michielsgestel</p>
              </div>
            </div>

            <div class="directions-card">
              <h4>Route vanuit Den Bosch</h4>
              <p><strong>Adres navigatie:</strong><br>
              Schijndelseweg 13A<br>
              5271 BK Sint-Michielsgestel</p>
              
              <p><strong>Reistijd:</strong><br>
              🚗 10-15 minuten<br>
              🚌 20-25 minuten</p>
              
              <p><strong>Parkeren:</strong><br>
              ✅ Gratis parkeren<br>
              ✅ Vlak voor de deur<br>
              ✅ Toegankelijk voor mindervaliden</p>
            </div>

            <div class="local-links">
              <h4>Andere locaties</h4>
              <ul>
                <li><router-link to="/tandarts-vught">Tandarts omgeving Vught</router-link></li>
                <li><router-link to="/tandarts-boxtel">Tandarts omgeving Boxtel</router-link></li>
                <li><router-link to="/tandarts-schijndel">Tandarts omgeving Schijndel</router-link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TandartsDenBosch',
  mounted() {
    // SEO optimization
    document.title = "Tandarts Den Bosch omgeving | Tandzorg Gestel Sint-Michielsgestel | Dichtbij 's-Hertogenbosch";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Tandarts gezocht in Den Bosch omgeving? Tandzorg Gestel in Sint-Michielsgestel ligt op 10 min van Den Bosch. Gratis parkeren, persoonlijke zorg. Nieuwe patiënten welkom!');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'tandarts Den Bosch, tandarts s-hertogenbosch, tandarts bij Den Bosch, Sint-Michielsgestel tandarts, Tandzorg Gestel, tandheelkunde Den Bosch');
    }
    
    // Schema.org structured data for location
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Tandzorg Gestel - Tandarts voor patiënten uit Den Bosch",
      "description": "Tandartspraktijk nabij Den Bosch in Sint-Michielsgestel. Ideaal voor inwoners van 's-Hertogenbosch die persoonlijke zorg zoeken.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Schijndelseweg 13A",
        "addressLocality": "Sint-Michielsgestel",
        "postalCode": "5271 BK",
        "addressCountry": "NL"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "51.6376708",
        "longitude": "5.3507692"
      },
      "telephone": "+31735515644",
      "areaServed": [
        "'s-Hertogenbosch",
        "Den Bosch",
        "Sint-Michielsgestel",
        "Vught",
        "Rosmalen"
      ],
      "@id": "https://tandzorggestel.nl/tandarts-den-bosch"
    });
    document.head.appendChild(script);
  }
}
</script>

<style scoped>
.location-page {
  padding-top: 88px;
}

.hero-section {
  background: linear-gradient(135deg, var(--blue), #0099a3);
  color: white;
  padding: 4rem 0;
  text-align: center;
}

.hero-section h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
}

.lead {
  font-size: 1.3rem;
  max-width: 700px;
  margin: 0 auto;
}

.content-section {
  padding: 4rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.row {
  display: flex;
  gap: 3rem;
}

.col-md-8 {
  flex: 2;
}

.col-md-4 {
  flex: 1;
}

.content-section h2 {
  color: var(--blue);
  font-size: 2.2rem;
  margin: 2rem 0 1rem 0;
}

.content-section h3 {
  color: var(--black);
  font-size: 1.8rem;
  margin: 1.5rem 0 1rem 0;
}

.content-section h4 {
  color: var(--blue);
  font-size: 1.6rem;
  margin: 1rem 0 0.5rem 0;
}

.content-section p {
  line-height: 1.8;
  margin-bottom: 1rem;
  color: var(--light-color);
  font-size: 1.6rem;
}

.content-section ul {
  margin: 1rem 0;
  padding-left: 2rem;
}

.content-section li {
  margin-bottom: 0.5rem;
  color: var(--light-color);
  font-size: 1.6rem;
  line-height: 1.6;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
}

.service-item {
  background: var(--light-bg);
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
}

.service-item h4 {
  margin-bottom: 1rem;
}

.service-link {
  display: inline-block;
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: var(--blue);
  color: white;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1.4rem;
  transition: all 0.3s ease;
}

.service-link:hover {
  background-color: var(--black);
  color: white;
  text-decoration: none;
}

.testimonials {
  margin: 2rem 0;
}

.testimonials blockquote {
  background: var(--light-bg);
  padding: 2rem;
  border-radius: 10px;
  margin: 1.5rem 0;
  border-left: 4px solid var(--blue);
  font-style: italic;
}

.testimonials cite {
  display: block;
  text-align: right;
  margin-top: 1rem;
  font-style: normal;
  color: var(--blue);
  font-weight: 600;
}

.appointment-card,
.directions-card {
  background: var(--light-bg);
  padding: 2rem;
  border-radius: 10px;
  margin-bottom: 2rem;
  box-shadow: var(--box-shadow);
}

.appointment-card h3,
.directions-card h4 {
  color: var(--blue);
  margin-bottom: 1rem;
}

.appointment-card .link-btn {
  display: block;
  margin-bottom: 1rem;
  text-align: center;
}

.contact-info {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #ddd;
}

.local-links {
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: var(--box-shadow);
}

.local-links h4 {
  color: var(--blue);
  margin-bottom: 1rem;
}

.local-links ul {
  list-style: none;
  padding: 0;
}

.local-links li {
  margin-bottom: 0.5rem;
}

.local-links a {
  color: var(--light-color);
  text-decoration: none;
  font-size: 1.4rem;
}

.local-links a:hover {
  color: var(--blue);
}

@media (max-width: 768px) {
  .row {
    flex-direction: column;
    gap: 2rem;
  }
  
  .hero-section h1 {
    font-size: 2.5rem;
  }
  
  .lead {
    font-size: 1.1rem;
  }
  
  .content-section {
    padding: 2rem 0;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
}
</style>
